import { Component, EventEmitter, Input, Output, ViewChild, ChangeDetectorRef } from '@angular/core';
import { forkJoin } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { ApiService } from '../../services/api.service';
import { AuthService } from '../../../core/services/auth.service';
import { MaskCollection } from '../../services/mask-collections.services';
import { Cliente, ClienteSelecao, ClientesSelecaoAtivarInativarCadastroEmLote, ResultadoAtivarInativarClientesEmLote } from '../../models/api-clients';
import { Paginador } from '../../models/paginador';
import { ConfirmPopupComponent } from '../confirm-popup/confirm-popup.component';
import AppResponseError from '../../models/app-response-error';

@Component({
  selector: 'confirmar-ativar-inativar-clientes',
  templateUrl: './confirmar-ativar-inativar-clientes.component.html',
  styleUrls: ['./confirmar-ativar-inativar-clientes.component.scss']
})
export class ConfirmarAtivarInativarClientesComponent {

  @Input() clientes: ClienteSelecao[] = [];
  @Input() modo: 'ativar' | 'inativar';
  @Input() licencasDisponiveis: number = 0;
  @Input() pularConfirmacaoInativar: boolean = false;
  @Input() pularConfirmacaoAtivar: boolean = false;
  @Input() exibirModalResultado: boolean = true;
  @Input() manutencaoEmLote: boolean = false;

  @Output() resultado = new EventEmitter<ResultadoAtivarInativarClientesEmLote[]>();
  @Output() fechado = new EventEmitter<void>();
  @Output() cancelado = new EventEmitter<void>();

  @ViewChild('confirmPopupAtivar') confirmPopupAtivar!: ConfirmPopupComponent;
  @ViewChild('confirmPopupInativar') confirmPopupInativar!: ConfirmPopupComponent;
  @ViewChild('confirmPopupLicencas') confirmPopupLicencas!: ConfirmPopupComponent;

  resultados: ResultadoAtivarInativarClientesEmLote[] = [];
  modalResultadoAberto = false;
  qtdePagina = 10;
  paginaAtualResultado = 0;

  get textoConfirmacaoInativacao(): string {
    const quantidade = this.clientes?.length || 0;

    return `${quantidade > 1
      ? "Ao inativar o(s) cadastro(s) acima, as obrigações serão desmarcadas e as licenças devolvidas"
      : "Ao inativar este cadastro deseja retomar as licenças consumidas por obrigações vinculadas ao cadastro?"}
        <br>
        <br>
        <strong>Atenção:</strong> Para as empresas na qual a licença foi ativada dentro dos últimos 30 dias não será possível retomar a licença. Ao retomar as licenças, o histórico de processamento não será perdido!`;
  }

  constructor(
    private apiService: ApiService,
    private authService: AuthService,
    private toastr: ToastrService,
    public maskCollection: MaskCollection,
    private cdr: ChangeDetectorRef
  ) {}

  get titulo(): string {
    return this.modo === 'ativar' ? 'Ativação de Cadastro(s)' : 'Inativação de Cadastro(s)';
  }

  get paginaResultado(): Paginador<ResultadoAtivarInativarClientesEmLote> {
    return {
      pagina: this.paginaAtualResultado,
      quantidadeNaPagina: this.qtdePagina,
      conteudo: this.resultados?.slice(this.paginaAtualResultado * this.qtdePagina, (this.paginaAtualResultado + 1) * this.qtdePagina),
      totalConteudo: this.resultados?.length
    };
  }

  iniciar(): void {
    if (!this.clientes.length) {
      this.toastr.warning('Nenhuma empresa selecionada', 'Atenção');
      return;
    }

    if (this.modo === 'ativar') {
      if (this.clientes.length > this.licencasDisponiveis) {
        this.toastr.error('A quantidade de licenças necessárias para a ativação das empresas é insuficiente!', this.titulo);
        return;
      }
      if (this.pularConfirmacaoAtivar) {
        this.executarAcao(false);
      } else {
        this.confirmPopupAtivar.abrirDialog();
      }
    } else if (this.pularConfirmacaoInativar) {
      this.confirmPopupLicencas.abrirDialog();
    } else {
      this.confirmPopupInativar.abrirDialog();
    }
  }

  onConfirmarAtivar = (): void => {
    this.executarAcao(false);
  };

  onConfirmarInativar = (): void => {
    this.confirmPopupLicencas.abrirDialog();
  };

  onRejeitarInativar = (): void => {
    this.cancelado.emit();
  };

  onConfirmarDevolverLicencas = (): void => {
    if (!this.clientes.length) {
      return;
    }

    const checagens = this.clientes.map(cliente => this.apiService.clienteCumpriuCarencia(cliente.id));

    forkJoin(checagens).subscribe({
      next: (carencias) => {
        const nomeEmpresasPendenciasContribuicoes = this.clientes
          .filter((_, i) => carencias[i].cumpriuCarenciaObrigacaoContribuicoes === 0)
          .map(c => c.nome);

        const nomesEmpresasPendenciasIcms = this.clientes
          .filter((_, i) => carencias[i].cumpriuCarenciaObrigacaoIcms === 0)
          .map(c => c.nome);

        const mensagemValidacaoCarenciaTitulo: string = 'Status alterado com sucesso!';
        var retomarLicencas: boolean = true;

        if (nomeEmpresasPendenciasContribuicoes.length && nomesEmpresasPendenciasIcms.length) {
          var pendenciasAmbasObrigacoes = nomeEmpresasPendenciasContribuicoes.filter(nomeContribuicoes => nomesEmpresasPendenciasIcms.includes(nomeContribuicoes));
          this.toastr.warning(
            `Não foi possível retomar a licença de ambas as obrigações para ${pendenciasAmbasObrigacoes.join(', ')}. Motivo: Licença ativada nos últimos 30 dias`,
            mensagemValidacaoCarenciaTitulo
          );
          retomarLicencas = false;
        }

        if (nomeEmpresasPendenciasContribuicoes.length) {
          this.toastr.warning(
            `Não foi possível retomar a licença da Obrigação EFD Contribuição para ${nomeEmpresasPendenciasContribuicoes.join(', ')}. Motivo: Licença ativada nos últimos 30 dias`,
            mensagemValidacaoCarenciaTitulo
          );
          retomarLicencas = false;
        }

        if (nomesEmpresasPendenciasIcms.length) {
          this.toastr.warning(
            `Não foi possível retomar a licença da Obrigação EFD ICMS IPI para ${nomesEmpresasPendenciasIcms.join(', ')}. Motivo: Licença ativada nos últimos 30 dias`,
            mensagemValidacaoCarenciaTitulo
          );
          retomarLicencas = false;
        }

        this.executarAcao(retomarLicencas);
      },
      error: () => this.toastr.error('Erro ao verificar carência das licenças', this.titulo)
    });
  };

  onRejeitarDevolverLicencas = (): void => {
    this.executarAcao(false);
  };

  private executarAcao(retomarLicencas: boolean): void {
    if (!!this.manutencaoEmLote)
      this.executarAcaoEmLote(retomarLicencas);
    this.executarAcaoIndividual(retomarLicencas);
  }

  private executarAcaoIndividual(retomarLicencas: boolean): void {
    const cliente: Cliente = this.clientes[0];
    const valorAtivacao = this.modo === 'ativar';

    this.apiService.ativarInativarCliente(cliente.id, valorAtivacao, retomarLicencas).subscribe({
      next: (data: Cliente) => {
        this.clientes[0].isAtivado = data.isAtivado;
        this.clientes = [...this.clientes];

        const mensagemDescricao = retomarLicencas ? 'Licenças retomadas com sucesso' : '';
        this.toastr.success(mensagemDescricao, 'Status alterado com sucesso!');
      },
      error: (err: AppResponseError) => {
        this.toastr.error(err.descricao, err.titulo);
      }
    });
  }

  private executarAcaoEmLote(retomarLicencas: boolean): void {
    const payload: ClientesSelecaoAtivarInativarCadastroEmLote = {
      clientes: this.clientes,
      modo: this.modo,
      licencasNaoUsadas: this.licencasDisponiveis,
      usuarioId: this.authService.userId,
      usuarioNome: this.authService.username,
      retomarLicencas
    };

    this.apiService.ativarInativarEmpresaClientEmLote(payload).subscribe({
      next: (resultados) => {
        this.resultados = resultados;
        this.paginaAtualResultado = 0;
        const totalAlterados = resultados.filter(r => r.alterado).length;

        if (this.exibirModalResultado) {
          this.toastr.success(`${totalAlterados} empresa(s) alterada(s) com sucesso!`, this.titulo);
          this.modalResultadoAberto = true;
        } else {
          if (totalAlterados) {
            this.toastr.success(`${totalAlterados} empresa(s) alterada(s) com sucesso!`, this.titulo);
          }
          resultados
            .filter(r => !r.alterado)
            .forEach(r => this.toastr.error(r.justificativa || 'Não foi possível concluir a alteração.', r.nome));
        }

        this.resultado.emit(resultados);
        this.cdr.detectChanges();
      },
      error: () => this.toastr.error('Erro ao processar alteração', this.titulo)
    });
  }

  fecharModalResultado(): void {
    this.modalResultadoAberto = false;
    this.fechado.emit();
  }
}
